import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { bildLaden, dateiSenden, fehlertext } from "./api";
import { Eingabe, Feld, Fehler, Karte, Knopf, Textknopf } from "./ui";

/**
 * Die Abnahme durch den Kunden: Unterschrift und Name.
 *
 * Der Anlass ist der einmalige Auftrag. Bei einer Montage bestaetigt der Kunde
 * am Ende, dass die Arbeit gemacht ist; beim wiederkehrenden Rundgang steht
 * meist niemand daneben. Deshalb ist die Unterschrift freiwillig und steht
 * nicht im Weg: wer sie nicht braucht, reicht den Bericht ein wie bisher.
 *
 * **Der Name in Druckbuchstaben ist Pflicht.** Eine Unterschrift auf einem
 * Telefondisplay ist oft nicht zu entziffern, und ein Nachweis, bei dem
 * niemand mehr sagen kann, wer unterschrieben hat, ist keiner.
 *
 * **Gezeichnet wird mit Zeigerereignissen**, nicht mit Maus- und
 * Beruehrungsereignissen getrennt. Ein Finger, ein Stift und eine Maus sind
 * damit derselbe Fall, und das Feld verhaelt sich auf dem Telefon wie am
 * Rechner. touch-action: none haelt den Browser davon ab, die Zeichenbewegung
 * als Wischen zu deuten und die Seite zu scrollen.
 */

/** Zeichenflaeche in Bildpunkten. Bewusst gross genug fuer das PDF. */
const BREITE = 600;
const HOEHE = 200;

export function Unterschriftsfeld({
  pruefungId, vorhandenName, vorhandenMediaId, aenderbar, onGespeichert
}: {
  pruefungId: string;
  /** Name unter einer bereits geleisteten Unterschrift, sonst null. */
  vorhandenName: string | null;
  vorhandenMediaId: string | null;
  /** Nur solange der Bericht Entwurf ist. Danach gehoert sie zum Beleg. */
  aenderbar: boolean;
  onGespeichert: () => void;
}) {
  const { t } = useTranslation();
  const [offen, setOffen] = useState(false);
  const [name, setName] = useState(vorhandenName ?? "");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);
  const [gezeichnet, setGezeichnet] = useState(false);
  const [bild, setBild] = useState<string | null>(null);
  const flaeche = useRef<HTMLCanvasElement | null>(null);
  const zeichnet = useRef(false);

  // Das vorhandene Bild wird wie jedes andere geholt: der Browser haengt an
  // eine Bildanfrage keinen Authorization-Kopf, siehe bildLaden in api.ts.
  useEffect(() => {
    if (!vorhandenMediaId) { setBild(null); return; }
    let verweis: string | null = null;
    void bildLaden(vorhandenMediaId).then((u) => { verweis = u; setBild(u); }).catch(() => setBild(null));
    return () => { if (verweis) URL.revokeObjectURL(verweis); };
  }, [vorhandenMediaId]);

  function stiftAn(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = flaeche.current;
    if (!c) return;
    c.setPointerCapture(e.pointerId);
    zeichnet.current = true;
    const ctx = c.getContext("2d");
    if (!ctx) return;
    const p = punkt(c, e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  }

  function stiftBewegt(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!zeichnet.current) return;
    const c = flaeche.current;
    const ctx = c?.getContext("2d");
    if (!c || !ctx) return;
    const p = punkt(c, e);
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0e1726";
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setGezeichnet(true);
  }

  function stiftAus() {
    zeichnet.current = false;
  }

  function leeren() {
    const c = flaeche.current;
    c?.getContext("2d")?.clearRect(0, 0, BREITE, HOEHE);
    setGezeichnet(false);
  }

  async function speichern() {
    const c = flaeche.current;
    if (!c) return;
    if (name.trim().length < 2) { setFehler(t("unterschrift.nameFehlt")); return; }
    if (!gezeichnet) { setFehler(t("unterschrift.leer")); return; }
    setLaeuft(true);
    setFehler(null);
    try {
      /*
       * PNG und nicht JPEG: der Hintergrund bleibt durchsichtig, und im PDF
       * steht der Schriftzug damit auf dem Papier und nicht in einem weissen
       * Kasten mit sichtbarer Kante.
       */
      const blob = await new Promise<Blob | null>((fertig) => c.toBlob(fertig, "image/png"));
      if (!blob) throw new Error("canvas");
      const form = new FormData();
      form.append("name", name.trim());
      form.append("datei", blob, "unterschrift.png");
      await dateiSenden(`/inspections/${pruefungId}/unterschrift`, form);
      setOffen(false);
      onGespeichert();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Karte className="mt-6">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <h2 className="font-bold">{t("unterschrift.titel")}</h2>
        {aenderbar && !offen && (
          <Textknopf onClick={() => { setOffen(true); setFehler(null); }}>
            {vorhandenName ? t("unterschrift.erneut") : t("unterschrift.leisten")}
          </Textknopf>
        )}
      </div>
      <p className="mb-3 text-sm text-leise">{t("unterschrift.hinweis")}</p>
      <Fehler text={fehler} />

      {!offen && (
        vorhandenName ? (
          <div>
            {bild && (
              <img src={bild} alt="" className="max-h-24 w-auto" />
            )}
            <p className="mt-1 border-t border-linie pt-1 text-sm font-semibold">{vorhandenName}</p>
          </div>
        ) : (
          <p className="text-sm text-leise">{t("unterschrift.keine")}</p>
        )
      )}

      {offen && (
        <div>
          <Feld label={t("unterschrift.name")} hinweis={t("unterschrift.nameHinweis")} breit>
            <Eingabe value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" />
          </Feld>
          <div className="mt-3 rounded border border-linie bg-white">
            <canvas
              ref={flaeche}
              width={BREITE}
              height={HOEHE}
              className="block h-40 w-full touch-none"
              onPointerDown={stiftAn}
              onPointerMove={stiftBewegt}
              onPointerUp={stiftAus}
              onPointerCancel={stiftAus}
              onPointerLeave={stiftAus}
            />
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Knopf disabled={laeuft} onClick={() => void speichern()}>
              {laeuft ? t("aktion.speichert") : t("aktion.speichern")}
            </Knopf>
            <Knopf art="neben" onClick={leeren}>{t("unterschrift.leeren")}</Knopf>
            <Knopf art="neben" onClick={() => { setOffen(false); setFehler(null); }}>
              {t("aktion.abbrechen")}
            </Knopf>
          </div>
        </div>
      )}
    </Karte>
  );
}

/**
 * Vom Zeigerereignis zum Punkt auf der Zeichenflaeche.
 *
 * Die Flaeche wird per CSS auf die Breite des Bildschirms gezogen, ihre
 * inneren Bildpunkte bleiben aber 600 mal 200. Ohne diese Umrechnung liefe der
 * Strich neben dem Finger her, und zwar umso weiter, je schmaler das Geraet.
 */
function punkt(c: HTMLCanvasElement, e: React.PointerEvent<HTMLCanvasElement>) {
  const rahmen = c.getBoundingClientRect();
  return {
    x: ((e.clientX - rahmen.left) / rahmen.width) * c.width,
    y: ((e.clientY - rahmen.top) / rahmen.height) * c.height
  };
}
